import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Wifi, ShieldCheck, MapPin } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { getMe } from "../lib/api";
import { Logo } from "../components/Logo";
import { lsRemove, lsSet } from "../lib/safeStorage";
import LanguageToggle from "../components/LanguageToggle";

const HERO_IMG =
  "https://images.pexels.com/photos/9419385/pexels-photo-9419385.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

export default function Splash() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [checking, setChecking] = useState(true);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setChecking(false);
      return;
    }
    (async () => {
      const me = await getMe();
      if (me) {
        // Authed users belong on the home / admin shell — Splash is for new arrivals.
        if (me.role === "admin") {
          navigate("/admin", { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      }
      setChecking(false);
    })();
  }, [navigate]);

  const handleGoogle = () => {
    if (!agreed) return; // safety — button is disabled, but be explicit
    try {
      // Capture the consent timestamp locally; AuthCallback persists it to the user
      // record right after the OAuth handshake completes.
      const payload = { v: 1, at: new Date().toISOString() };
      lsSet("habano.legal_agreed", JSON.stringify(payload));
      lsSet("habano.legal_agreed_pending", payload.at);
    } catch { /* ignore */ }
    lsRemove("habano.guest");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/select-role";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const continueAsGuest = () => {
    // Belt-and-suspenders: write to safeStorage (which falls back to memory if
    // localStorage is blocked) AND pass ?guest=1 so the new index route still
    // detects the intent on a hard refresh.
    lsSet("habano.guest", "1");
    navigate("/?guest=1", { replace: true });
  };

  return (
    <main data-testid="splash-page" className="relative min-h-screen overflow-hidden pt-16 bg-background">
      {/* Cinematic hero image */}
      <div className="absolute inset-0 -z-10 hero-overlay">
        <img
          src={HERO_IMG}
          alt="Cigar lounge at night"
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 grain" />
      </div>

      <section className="max-w-6xl mx-auto px-6 sm:px-8 pt-12 sm:pt-20 pb-24 relative">
        {/* Language toggle pinned top-right */}
        <div className="absolute top-4 right-6 sm:right-8 z-10">
          <LanguageToggle size="sm" testIdPrefix="splash-lang" />
        </div>

        {/* Wordmark */}
        <div className="flex justify-center">
          <Logo size="lg" />
        </div>

        <div className="rule mt-16 mb-12 max-w-md mx-auto" />

        <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl text-center max-w-3xl mx-auto leading-[1.05] text-foreground">
          A private<span className="italic font-light"> Havana </span>
          <span className="text-gold">in your pocket.</span>
        </h1>

        <p className="mt-8 max-w-xl mx-auto text-center text-base sm:text-lg text-foreground/70 leading-relaxed">
          {t("tagline")}
        </p>

        <div className="mt-12 flex flex-col items-center gap-4">
          {/* Required agreement checkbox — gates account creation. */}
          <label
            htmlFor="legal-agree"
            data-testid="legal-agree-row"
            className="flex items-start gap-3 max-w-[320px] text-left cursor-pointer select-none"
            style={{ minHeight: 44 }}
          >
            <input
              id="legal-agree"
              type="checkbox"
              data-testid="legal-agree-checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-gold shrink-0 cursor-pointer"
              style={{ accentColor: "#E8B84B" }}
              aria-required="true"
            />
            <span className="text-xs text-foreground/70 leading-relaxed">
              I agree to the{" "}
              <Link to="/terms" data-testid="legal-agree-terms-link" className="text-gold underline-offset-2 hover:underline">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" data-testid="legal-agree-privacy-link" className="text-gold underline-offset-2 hover:underline">
                Privacy Policy
              </Link>
              .
            </span>
          </label>

          <button
            data-testid="continue-google-btn"
            onClick={handleGoogle}
            disabled={!agreed}
            aria-disabled={!agreed}
            title={agreed ? "" : "Please accept the Terms & Privacy Policy to continue"}
            className="btn-gold min-w-[280px] py-4 text-base disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <GoogleGlyph />
            <span>{t("continue_google")}</span>
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
          </button>

          <button
            data-testid="continue-guest-btn"
            onClick={continueAsGuest}
            className="inline-flex items-center justify-center gap-2 min-w-[280px] py-4 border border-gold-strong text-gold hover:bg-[rgba(232,184,75,0.08)] transition-colors font-sans text-base"
            style={{ minHeight: 48 }}
          >
            {t("auth_continue_guest")}
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>

          <p className="text-xs text-foreground/55 max-w-xs text-center mt-1">
            {t("splash_caption")}
          </p>

          {/* Small inline legal links visible on the login screen regardless of checkbox state. */}
          <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-foreground/45">
            <Link to="/privacy" data-testid="splash-footer-privacy" className="hover:text-gold transition-colors">Privacy</Link>
            <span className="mx-2">·</span>
            <Link to="/terms" data-testid="splash-footer-terms" className="hover:text-gold transition-colors">Terms</Link>
            <span className="mx-2">·</span>
            <span>© 2025 Habano App LLC</span>
          </div>
        </div>

        <div className="rule mt-24 mb-12 max-w-md mx-auto" />

        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <Feature icon={<Wifi className="w-5 h-5" strokeWidth={1.5} />} title="Low-bandwidth ready" body="Three image sizes, lazy loading, offline tiles."/>
          <Feature icon={<MapPin className="w-5 h-5" strokeWidth={1.5} />} title="On-the-ground" body="Vendors, lounges, and the streets that connect them."/>
          <Feature icon={<ShieldCheck className="w-5 h-5" strokeWidth={1.5} />} title="Trusted by design" body="Soft deletes, audit trail, three-review threshold."/>
        </ul>

        {checking && <div className="mt-12 text-xs text-foreground/45 text-center" data-testid="checking-session">…</div>}
      </section>
    </main>
  );
}

const Feature = ({ icon, title, body }) => (
  <li className="lux-card p-6 text-center sm:text-left">
    <div className="text-gold mb-3 flex justify-center sm:justify-start">{icon}</div>
    <div className="font-display text-xl text-foreground">{title}</div>
    <p className="text-sm text-foreground/60 mt-2 leading-relaxed">{body}</p>
  </li>
);

const GoogleGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.3 0-11.5-5.2-11.5-11.5S17.7 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.6 6.4 29 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.4-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.6 6.9 29 5 24 5 16.3 5 9.7 9 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c4.9 0 9.4-1.8 12.8-4.8l-6-5c-1.9 1.4-4.3 2.3-6.8 2.3-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.6 39.9 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6 5c-.4.4 6.6-4.8 6.6-14.6 0-1.2-.1-2.4-.4-3.5z"/>
  </svg>
);
